// Creator handles without a game (accounts wave 6): a signed-in user claims `cr_<handle>` to publish assets before
// (or without) having a game in the catalogue. POST /v1/creators {handle, name}; GET /v1/creators/available?handle=.

export const HANDLE_RULES = "3 to 32 characters: lowercase letters, digits and single hyphens, not at the start or end.";

const HANDLE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Checked here first so people get the reason while typing; accounts stays the authority. */
export function checkHandle(handle: string): "invalid_handle" | null {
  return handle.length >= 3 && handle.length <= 32 && HANDLE.test(handle) ? null : "invalid_handle";
}

/** The specific rule a handle breaks, in plain words (null when it's fine). */
export function handleProblem(handle: string): string | null {
  if (!handle) return null;
  if (handle.length < 3) return "At least 3 characters.";
  if (handle.length > 32) return "At most 32 characters.";
  if (/[A-Z]/.test(handle)) return "Lowercase letters only.";
  if (/[^a-z0-9-]/.test(handle)) return "Only letters, digits and hyphens.";
  if (/--/.test(handle)) return "One hyphen at a time.";
  if (/^-|-$/.test(handle)) return "Not starting or ending with a hyphen.";
  return checkHandle(handle) ? HANDLE_RULES : null;
}

export function handleMessage(code: string | null): string | null {
  if (!code) return null;
  const m: Record<string, string> = {
    invalid_handle: `That handle doesn't fit the rules: ${HANDLE_RULES}`,
    handle_reserved: "That handle is reserved. Pick another.",
    handle_taken: "Someone already has that handle. Pick another.",
    handle_limit: "You already have a creator handle. Each account gets one; ask us if you need another.",
  };
  return m[code] ?? null;
}

/** accounts' availability answer, read defensively; null when it's about another handle (a stale reply). */
export function parseAvailable(raw: unknown, asked: string): { available: boolean; reason: string | null } | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.handle === "string" && r.handle !== asked) return null;
  return { available: r.available === true, reason: typeof r.reason === "string" ? r.reason : null };
}
